import formatCallDuration from '../../utils/formatCallDuration';
import useCallDuration from '../../hooks/useCallDuration';
import React, { useState, useEffect, useRef } from 'react';
import {
    Room,
    RoomEvent,
    Track,
    createLocalTracks,
    VideoPresets,
} from 'livekit-client';
import {
    FaMicrophone, FaMicrophoneSlash,
    FaVideo, FaVideoSlash,
    FaPhoneSlash, FaExpand, FaCompress, FaDesktop,
    FaUsers, FaUserPlus,
} from 'react-icons/fa';
import ParticipantPanel from './ParticipantPanel';
import AddMembersModal from './AddMembersModal';
import './AgoraActiveCall.css';

// Audio stays mounted for voice calls and while a participant shares a screen.
const RemoteAudio = ({ participant }) => {
    const container = useRef(null);
    useEffect(() => {
        const attached = new Map();
        const sync = () => {
            const tracks = new Set(Array.from(participant.trackPublications.values())
                .filter(pub => pub.kind === Track.Kind.Audio && pub.track).map(pub => pub.track));
            attached.forEach((element, track) => {
                if (!tracks.has(track)) {
                    track.detach(element);
                    element.remove();
                    attached.delete(track);
                }
            });
            tracks.forEach(track => {
                if (attached.has(track)) return;
                const element = track.attach();
                container.current.appendChild(element);
                attached.set(track, element);
            });
        };
        sync();
        participant.on('trackSubscribed', sync);
        participant.on('trackUnsubscribed', sync);
        return () => {
            participant.off('trackSubscribed', sync);
            participant.off('trackUnsubscribed', sync);
            attached.forEach((element, track) => { track.detach(element); element.remove(); });
        };
    }, [participant]);
    return <div ref={container} className="remote-audio" />;
};

const disconnectRoom = room => {
    room.localParticipant.trackPublications.forEach(pub => {
        try {
            pub.track?.stop?.();
            pub.track?.mediaStreamTrack?.stop();
        } catch { /* Continue releasing other tracks. */ }
    });
    try { return Promise.resolve(room.disconnect()).catch(() => {}); }
    catch { return Promise.resolve(); }
};

const ScreenShareView = ({ participant }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const attachTrack = () => {
            const pub = Array.from(participant.trackPublications.values()).find(
                p => p.source === Track.Source.ScreenShare
            );
            if (pub && pub.track && videoRef.current) {
                pub.track.attach(videoRef.current);
            }
        };

        attachTrack();
        participant.on('trackSubscribed', attachTrack);
        return () => participant.off('trackSubscribed', attachTrack);
    }, [participant]);

    return (
        <div className="screen-share-container">
            <video ref={videoRef} autoPlay playsInline className="screen-share-video" />
            <div className="screen-share-label">
                {participant.isLocal ? 'Your Screen' : `${participant.name || participant.identity}'s Screen`}
            </div>
        </div>
    );
};

const RemoteParticipantView = ({ participant, isSpeaking }) => {
    const videoRef = useRef(null);
    const [hasVideo, setHasVideo] = useState(false);
    const [hasAudio, setHasAudio] = useState(false);

    useEffect(() => {
        const attachTracks = () => {
            let foundVideo = false;
            let foundAudio = false;

            participant.trackPublications.forEach(pub => {
                const track = pub.track;
                if (!track) return;

                // Only attach camera video here, screen share is handled separately
                if (pub.kind === Track.Kind.Video && pub.source === Track.Source.Camera && videoRef.current) {
                    track.attach(videoRef.current);
                    foundVideo = true;
                }

                if (pub.kind === Track.Kind.Audio) {
                    foundAudio = true;
                }
            });

            setHasVideo(foundVideo);
            setHasAudio(foundAudio);
        };

        attachTracks();
        participant.on('trackSubscribed', attachTracks);
        participant.on('trackUnsubscribed', attachTracks);

        return () => {
            participant.off('trackSubscribed', attachTracks);
            participant.off('trackUnsubscribed', attachTracks);
        };
    }, [participant]);

    return (
        <div className={`remote-user ${isSpeaking ? 'speaking' : ''}`}>
            <video ref={videoRef} autoPlay playsInline className="remote-video" />
            {!hasVideo && (
                <div className="video-off-placeholder">
                    <FaVideoSlash size={32} />
                    <span>Camera Off</span>
                </div>
            )}
            <div className="user-info">
                <span>{participant.name || participant.identity}</span>
                {!hasAudio && <FaMicrophoneSlash size={12} />}
            </div>
        </div>
    );
};

const LiveKitActiveCall = ({ meeting, tokens, callType = 'video', onLeave, onDurationChange, onParticipantCountChange, initialAudioMuted = false, initialVideoMuted = false }) => {
    const isVoiceOnly = callType === 'voice';
    const [localAudioMuted, setLocalAudioMuted] = useState(initialAudioMuted);
    const [localVideoMuted, setLocalVideoMuted] = useState(initialVideoMuted);
    const [remoteParticipants, setRemoteParticipants] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [connectionState, setConnectionState] = useState('connecting');
    const callDuration = useCallDuration(tokens, onDurationChange, connectionState);
    const [isSharingScreen, setIsSharingScreen] = useState(false);
    const [screenShareParticipant, setScreenShareParticipant] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [speakingParticipants, setSpeakingParticipants] = useState(new Set());
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [showParticipants, setShowParticipants] = useState(false);
    const [showAddMembers, setShowAddMembers] = useState(false);
    const [invitedUsers, setInvitedUsers] = useState([]);

    const containerRef = useRef(null);
    const localVideoRef = useRef(null);
    const roomRef = useRef(null);
    const localTracksRef = useRef({ audio: null, video: null });
    const isMounted = useRef(true);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const animFrameRef = useRef(null);
    const leaving = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        let cancelled = false;
        
        const timeSinceLastLeave = Date.now() - (window._lastVideoCallLeaveTime || 0);
        const delay = timeSinceLastLeave < 2000 ? 1500 : 0;
        
        const startupTimer = setTimeout(() => {
            if (!cancelled) initializeLiveKit(() => !cancelled);
        }, delay);

        const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onFsChange);

        return () => {
            isMounted.current = false;
            cancelled = true;
            clearTimeout(startupTimer);
            document.removeEventListener('fullscreenchange', onFsChange);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (audioCtxRef.current) audioCtxRef.current.close();

            const currentRoom = roomRef.current;
            const { audio, video } = localTracksRef.current;
            if (audio) { try { audio.stop(); } catch { /* silent */ } }
            if (video) { try { video.stop(); } catch { /* silent */ } }
            if (currentRoom) {
                currentRoom.removeAllListeners();
                try {
                    const lp = currentRoom.localParticipant;
                    if (lp) {
                        for (const pub of lp.trackPublications.values()) {
                            try { pub.track?.mediaStreamTrack?.stop(); } catch { /* silent */ }
                        }
                    }
                    currentRoom.disconnect();
                } catch { /* silent */ }
                if (roomRef.current === currentRoom) roomRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (onParticipantCountChange) {
            onParticipantCountChange(remoteParticipants.length + 1);
        }
    }, [remoteParticipants.length]);

    const syncParticipants = () => {
        if (!roomRef.current || !isMounted.current) return;
        const participants = Array.from(roomRef.current.remoteParticipants.values());
        setRemoteParticipants(participants);

        // Check if anyone is sharing screen (including local)
        let sharingPart = null;
        if (roomRef.current.localParticipant.isScreenShareEnabled) {
            sharingPart = roomRef.current.localParticipant;
        } else {
            sharingPart = participants.find(p => 
                Array.from(p.trackPublications.values()).some(pub => pub.source === Track.Source.ScreenShare && pub.isSubscribed)
            );
        }
        setScreenShareParticipant(sharingPart);
        
        // Ensure local video is attached if enabled
        if (roomRef.current.localParticipant.isCameraEnabled && localVideoRef.current) {
            const videoPub = roomRef.current.localParticipant.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.videoTrack) {
                videoPub.videoTrack.attach(localVideoRef.current);
            }
        }
    };

    const initializeLiveKit = async (isActive) => {
        let room = null;
        try {
            room = new Room({
                adaptiveStream: true,
                dynacast: true,
                videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
            });
            roomRef.current = room;
            const isCurrent = () => isActive() && !leaving.current && roomRef.current === room;
            const abandonIfClosed = () => {
                if (isCurrent()) return false;
                disconnectRoom(room);
                return true;
            };

            room.on(RoomEvent.ParticipantConnected, syncParticipants);
            room.on(RoomEvent.ParticipantDisconnected, syncParticipants);
            room.on(RoomEvent.TrackSubscribed, syncParticipants);
            room.on(RoomEvent.TrackUnsubscribed, syncParticipants);
            room.on(RoomEvent.LocalTrackPublished, (pub) => {
                syncParticipants();
                if (pub.source === Track.Source.Camera && pub.videoTrack && localVideoRef.current) {
                    pub.videoTrack.attach(localVideoRef.current);
                }
                if (pub.source === Track.Source.Microphone && pub.track?.mediaStreamTrack) {
                    startLocalAudioAnalyser(room);
                }
            });
            room.on(RoomEvent.LocalTrackUnpublished, syncParticipants);
            room.on(RoomEvent.ConnectionStateChanged, (state) => {
                if (roomRef.current === room) setConnectionState(state);
            });
            room.on(RoomEvent.Reconnecting, () => {
                if (isMounted.current) setIsReconnecting(true);
            });
            room.on(RoomEvent.Reconnected, () => {
                if (isMounted.current) setIsReconnecting(false);
            });
            room.on(RoomEvent.Disconnected, () => {
                if (!isCurrent()) return;
                leaving.current = true;
                onLeave();
            });
            room.on(RoomEvent.DataReceived, (payload, sender) => {
                try {
                    const senderIsHost = sender?.identity === String(meeting.host?.id);
                    const msg = JSON.parse(new TextDecoder().decode(payload));
                    if (msg.type === 'mute-request' && senderIsHost && msg.target === room.localParticipant.identity) {
                        room.localParticipant.setMicrophoneEnabled(false);
                        setLocalAudioMuted(true);
                        stopLocalAudioAnalyser();
                    }
                } catch { /* ignore malformed messages */ }
            });
            room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
                const localIsSpeaking = speakers.some(s => s.isLocal);
                setIsSpeaking(localIsSpeaking);
                const ids = new Set(speakers.filter(s => !s.isLocal).map(s => s.identity));
                setSpeakingParticipants(ids);
            });

            await room.connect(tokens.serverUrl, tokens.token);

            if (abandonIfClosed()) return;
            setConnectionState('connected');

            // Use LiveKit's built-in participant methods instead of manual track creation
            // This ensures hardware lights turn off and state stays synced
            try {
                if (!initialAudioMuted) {
                    await room.localParticipant.setMicrophoneEnabled(true);
                    // analyser starts on LocalTrackPublished event below
                }
            } catch (err) {
                console.error("Failed to enable mic:", err);
                setLocalAudioMuted(true);
            }
            if (abandonIfClosed()) return;

            if (!isVoiceOnly) {
                try {
                    if (!initialVideoMuted) {
                        await room.localParticipant.setCameraEnabled(true);
                    }
                } catch (err) {
                    console.error("Failed to enable camera:", err);
                    setLocalVideoMuted(true);
                }
            } else {
                setLocalVideoMuted(true);
            }
            if (abandonIfClosed()) return;

            // If we have a local video ref and the camera is enabled, the track will be handled by LiveKit events
            // But we can attach the first video track if it exists
            const videoPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.videoTrack && localVideoRef.current) {
                videoPub.videoTrack.attach(localVideoRef.current);
            }

            syncParticipants();
        } catch (err) {
            console.error('[LiveKit] init failed:', err);
            if (roomRef.current === room) setConnectionState('failed');
        }
    };

    const startLocalAudioAnalyser = (room) => {
        const micPub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        const mediaStreamTrack = micPub?.track?.mediaStreamTrack;
        if (!mediaStreamTrack) return;

        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        if (audioCtxRef.current) audioCtxRef.current.close();

        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') audioCtx.resume();
        audioCtxRef.current = audioCtx;

        const stream = new MediaStream([mediaStreamTrack]);
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
            if (!isMounted.current) return;
            analyser.getByteFrequencyData(dataArray);
            const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            setAudioLevel(Math.min(avg / 60, 1));
            animFrameRef.current = requestAnimationFrame(tick);
        };
        tick();
    };

    const handleMuteRequest = async (targetIdentity) => {
        if (!roomRef.current) return;
        const payload = new TextEncoder().encode(
            JSON.stringify({ type: 'mute-request', target: targetIdentity })
        );
        await roomRef.current.localParticipant.publishData(payload, { reliable: true });
    };

    const stopLocalAudioAnalyser = () => {
        if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
        if (audioCtxRef.current) { audioCtxRef.current.close(); audioCtxRef.current = null; }
        setAudioLevel(0);
    };

    const cleanupRoom = async () => {
        const room = roomRef.current;
        roomRef.current = null;
        if (!room) return;
        room.removeAllListeners();
        await disconnectRoom(room);
    };

    const toggleAudio = async () => {
        if (!roomRef.current) return;
        const next = !localAudioMuted;
        try {
            await roomRef.current.localParticipant.setMicrophoneEnabled(!next);
            setLocalAudioMuted(next);
            if (next) {
                stopLocalAudioAnalyser();
            } else {
                startLocalAudioAnalyser(roomRef.current);
            }
        } catch (error) {
            console.error("Failed to toggle mic:", error);
            setLocalAudioMuted(!roomRef.current.localParticipant.isMicrophoneEnabled);
        }
    };

    const toggleVideo = async () => {
        if (!roomRef.current) return;
        const next = !localVideoMuted;
        try {
            await roomRef.current.localParticipant.setCameraEnabled(!next);
            setLocalVideoMuted(next);
            
            // Re-attach if turning on
            if (!next && localVideoRef.current) {
                const videoPub = roomRef.current.localParticipant.getTrackPublication(Track.Source.Camera);
                if (videoPub && videoPub.videoTrack) {
                    videoPub.videoTrack.attach(localVideoRef.current);
                }
            }
        } catch (error) {
            console.error("Failed to toggle camera:", error);
            setLocalVideoMuted(!roomRef.current.localParticipant.isCameraEnabled);
        }
    };

    const toggleScreenShare = async () => {
        if (!roomRef.current) return;
        
        // If someone else is already sharing, don't allow local sharing
        const remoteSharing = remoteParticipants.some(p => 
            Array.from(p.trackPublications.values()).some(pub => pub.source === Track.Source.ScreenShare)
        );
        
        if (!isSharingScreen && remoteSharing) {
            alert("Someone else is already sharing their screen.");
            return;
        }

        try {
            await roomRef.current.localParticipant.setScreenShareEnabled(!isSharingScreen);
            setIsSharingScreen(!isSharingScreen);
            syncParticipants();
        } catch (err) {
            console.error('[LiveKit] screen share failed:', err);
        }
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    const handleLeave = async () => {
        if (leaving.current) return;
        leaving.current = true;
        window._lastVideoCallLeaveTime = Date.now();
        await cleanupRoom();
        onLeave();
    };



    const buildLiveKitParticipants = () => {
        const room = roomRef.current;
        if (!room) return [];

        const local = room.localParticipant;
        const localEntry = {
            identity: local.identity,
            name: local.name || local.identity,
            isMuted: !local.isMicrophoneEnabled,
            isSpeaking: isSpeaking,
            role: meeting.permissions?.is_host ? 'host' : 'participant',
        };

        const remoteEntries = remoteParticipants.map(p => ({
            identity: p.identity,
            name: p.name || p.identity,
            isMuted: !Array.from(p.trackPublications.values()).some(
                pub => pub.kind === 'audio' && pub.isSubscribed && !pub.isMuted
            ),
            isSpeaking: speakingParticipants.has(p.identity),
            role: 'participant',
        }));

        const activeIds = new Set([localEntry.identity, ...remoteEntries.map(r => String(r.identity))]);
        const invitedEntries = invitedUsers
            .filter(u => !activeIds.has(String(u.id)))
            .map(u => ({
                identity: String(u.id),
                name: u.name,
                isMuted: true,
                isSpeaking: false,
                role: 'invited',
            }));

        return [localEntry, ...remoteEntries, ...invitedEntries];
    };

    return (
        <div ref={containerRef} className="active-call-container">
            {isReconnecting && (
                <div className="reconnecting-banner">
                    <span className="reconnecting-spinner" />
                    Reconnecting…
                </div>
            )}
            {connectionState === 'connecting' && (
                <div className="call-connecting-overlay">
                    <p>Connecting...</p>
                </div>
            )}
            {connectionState === 'failed' && (
                <div className="call-connecting-overlay" style={{ flexDirection: 'column', gap: '1rem' }}>
                    <p>Connection failed. Please leave and try again.</p>
                    <button className="control-btn leave danger" onClick={handleLeave} title="Leave call">
                        <FaPhoneSlash />
                    </button>
                </div>
            )}

            <div className="call-timer">{formatCallDuration(callDuration)}</div>

            <div className={`video-grid${isVoiceOnly ? ' voice-only-grid' : ''}`}>
                {remoteParticipants.map(participant => <RemoteAudio key={`audio-${participant.identity}`} participant={participant} />)}
                {isVoiceOnly ? (
                    <div className="voice-call-participants">
                        <div className={`voice-participant local`}>
                            <div className={`voice-avatar ${isSpeaking ? 'speaking' : ''}`}>
                                <FaMicrophone size={28} />
                            </div>
                            <span>You {localAudioMuted && <FaMicrophoneSlash size={12} />}</span>
                        </div>
                        {remoteParticipants.length === 0 ? (
                            <div className="empty-call-state"><p>Waiting for others to join...</p></div>
                        ) : (
                            remoteParticipants.map(participant => (
                                <div key={participant.identity} className={`voice-participant ${speakingParticipants.has(participant.identity) ? 'speaking' : ''}`}>
                                    <div className={`voice-avatar ${speakingParticipants.has(participant.identity) ? 'speaking' : ''}`}>
                                        <FaMicrophone size={28} />
                                    </div>
                                    <span>{participant.name || participant.identity}</span>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <>
                        <div className={`remote-users ${screenShareParticipant ? 'with-screen-share' : ''}`}>
                            {screenShareParticipant && (
                                <div className="screen-share-view">
                                    <ScreenShareView participant={screenShareParticipant} />
                                </div>
                            )}
                            <div className="participants-grid">
                                {remoteParticipants.length === 0 && !isSharingScreen ? (
                                    <div className="empty-call-state">
                                        <p>Waiting for others to join...</p>
                                    </div>
                                ) : (
                                    remoteParticipants
                                        .filter(p => p !== screenShareParticipant)
                                        .map(participant => (
                                            <RemoteParticipantView
                                                key={participant.identity}
                                                participant={participant}
                                                isSpeaking={speakingParticipants.has(participant.identity)}
                                            />
                                        ))
                                )}
                            </div>
                        </div>

                        <div className="local-user-pip">
                            <video
                                ref={localVideoRef}
                                autoPlay
                                muted
                                playsInline
                                className={`local-video${localVideoMuted ? ' local-video-hidden' : ''}`}
                            />
                            {localVideoMuted && (
                                <div className="video-off-overlay">
                                    <FaVideoSlash size={24} />
                                </div>
                            )}
                            <div className="local-user-label">
                                You {localAudioMuted && <FaMicrophoneSlash size={12} />}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="call-controls">
                <button
                    className={`control-btn ${localAudioMuted ? 'muted' : ''} ${!localAudioMuted && isSpeaking ? 'speaking' : ''}`}
                    style={!localAudioMuted ? { '--mic-level': audioLevel } : undefined}
                    onClick={toggleAudio}
                    title={localAudioMuted ? 'Unmute' : 'Mute'}
                >
                    {localAudioMuted ? <FaMicrophoneSlash /> : <FaMicrophone />}
                </button>

                {!isVoiceOnly && (
                    <button
                        className={`control-btn ${localVideoMuted ? 'muted' : ''}`}
                        onClick={toggleVideo}
                        title={localVideoMuted ? 'Start Video' : 'Stop Video'}
                    >
                        {localVideoMuted ? <FaVideoSlash /> : <FaVideo />}
                    </button>
                )}

                {!isVoiceOnly && (
                    <button
                        className={`control-btn ${isSharingScreen ? 'active' : ''}`}
                        onClick={toggleScreenShare}
                        title={isSharingScreen ? 'Stop Sharing' : 'Share Screen'}
                    >
                        <FaDesktop />
                    </button>
                )}

                <button
                    className="control-btn"
                    onClick={toggleFullscreen}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>

                <button
                    className="control-btn leave-btn"
                    onClick={handleLeave}
                    title="Leave Call"
                >
                    <FaPhoneSlash />
                </button>
            </div>

            <div className="call-icon-sidebar">
                <button
                    className={`sidebar-icon-btn ${showParticipants ? 'active' : ''}`}
                    onClick={() => setShowParticipants(p => !p)}
                    title="Participants"
                >
                    <FaUsers size={16} />
                </button>
                {meeting.permissions?.is_host && (
                    <button
                        className="sidebar-icon-btn"
                        onClick={() => setShowAddMembers(true)}
                        title="Add member"
                    >
                        <FaUserPlus size={16} />
                    </button>
                )}
            </div>

            {showParticipants && (
                <ParticipantPanel
                    participants={buildLiveKitParticipants()}
                    localIdentity={roomRef.current?.localParticipant?.identity || ''}
                    isHost={meeting.permissions?.is_host || false}
                    onMuteRequest={handleMuteRequest}
                    onClose={() => setShowParticipants(false)}
                />
            )}

            {showAddMembers && (
                <AddMembersModal
                    meeting={meeting}
                    existingParticipantIds={[...buildLiveKitParticipants().map(p => p.identity), ...invitedUsers.map(u => String(u.id))]}
                    onClose={() => setShowAddMembers(false)}
                    onAdded={(newUsers) => {
                        if (newUsers && newUsers.length) {
                            setInvitedUsers(prev => {
                                const map = new Map(prev.map(u => [u.id, u]));
                                newUsers.forEach(u => map.set(u.id, u));
                                return Array.from(map.values());
                            });
                        }
                        setShowAddMembers(false);
                    }}
                />
            )}
        </div>
    );
};


export default LiveKitActiveCall;
